import clsx from 'clsx';
import { forwardRef, type ReactElement, type Ref } from 'react';
import { FormattedMessage } from 'react-intl';

import {
  type DataTestId,
  type StylingProps,
  type WithChildren,
} from '@dynatrace/strato-components/core';
import { _canReceiveDefaultTextFormat } from '@dynatrace/strato-components/core';
import { Flex } from '@dynatrace/strato-components/layouts';
import { Text } from '@dynatrace/strato-components/typography';

import { chipWrapperCSS, chipCSS } from './DataTableV2SelectionChip.sty.js';
import { useDataTableV2 } from '../../contexts/DataTableV2Context.js';

/**
 * @public
 */
export type DataTableV2SelectionChipProps = WithChildren &
  StylingProps &
  DataTestId;

/**
 * A helper function to check whether the child component is the SelectionChip component
 * @internal
 */
export function _isDataTableV2SelectionChip(
  child: ReactElement | null,
): child is ReactElement<DataTableV2SelectionChipProps> {
  return child?.type === DataTableV2SelectionChip;
}

/**
 *
 * @public
 */
export const DataTableV2SelectionChip = forwardRef(
  (props: DataTableV2SelectionChipProps, forwardedRef: Ref<HTMLDivElement>) => {
    const {
      children,
      'data-testid': dataTestId,
      className: consumerClassName,
      style: consumerStyle,
      ...remainingProps
    } = props;

    const table = useDataTableV2();
    const rowsCount = table.getSelectionCount();
    const rowDensity = table.getVisualVariantOptions().rowDensity;

    return (
      <Flex
        ref={forwardedRef}
        className={chipWrapperCSS[rowDensity]}
        marginLeft={table.getVisualVariantOptions().contained ? 2 : 0}
      >
        <Flex
          data-testid={dataTestId}
          className={clsx([chipCSS, consumerClassName])}
          style={consumerStyle}
          {...remainingProps}
        >
          {rowsCount}
        </Flex>
        {!_canReceiveDefaultTextFormat(children) ? (
          children
        ) : (
          <Text as="p" textStyle="base">
            {children ?? (
              <FormattedMessage
                defaultMessage="selected"
                id="xFQW8ah15nm5hjiI"
                description="Message to display for the total count of selected rows."
              />
            )}
          </Text>
        )}
      </Flex>
    );
  },
);

(
  DataTableV2SelectionChip as typeof DataTableV2SelectionChip & {
    displayName: string;
  }
).displayName = 'DataTableV2.SelectionChip';
